import { useEffect, useState } from 'react';
import { Hint, Panel } from './ui/Controls';
import { store, useStore } from '@/state/store';
import { refreshLayout } from '@/engine/pipeline';
import { STYLE_LIST } from '@/engine/layout/styles';
import { detectHelveticaNeue, HN_WEIGHTS, type FontStatus } from '@/lib/fonts';
import type { StyleId } from '@/types';

export function DesignStyleSelector() {
  const styleId = useStore((s) => s.styleId);
  const hasVariants = useStore((s) => s.variants.length > 0);
  const [fontStatus, setFontStatus] = useState<FontStatus | null>(null);

  useEffect(() => {
    // Wait for font loading to settle before probing, or every weight reports
    // as missing on a cold start.
    let cancelled = false;
    const probe = () => {
      if (!cancelled) setFontStatus(detectHelveticaNeue());
    };
    if (document.fonts?.ready) void document.fonts.ready.then(probe);
    else probe();
    return () => {
      cancelled = true;
    };
  }, []);

  const select = (id: StyleId) => {
    store.commit({ styleId: id });
    if (hasVariants) void refreshLayout();
  };

  const active = STYLE_LIST.find((s) => s.id === styleId);

  return (
    <Panel title="Design style">
      <div className="grid grid-cols-3 gap-1">
        {STYLE_LIST.map((style) => (
          <button
            key={style.id}
            type="button"
            title={style.blurb}
            onClick={() => select(style.id)}
            className={`rounded-md px-1.5 py-2 text-[10px] leading-tight transition ${
              styleId === style.id
                ? 'bg-accent text-ink-950'
                : 'bg-white/[0.03] text-ink-300 hover:bg-white/[0.07]'
            }`}
          >
            {style.label}
          </button>
        ))}
      </div>

      {active && (
        <p className="mt-2.5 text-[10px] leading-relaxed text-ink-400">
          {active.blurb}{' '}
          <span className="text-ink-500">
            Helvetica Neue {weightName(active.headlineWeight)} ·{' '}
            {active.headlineCase === 'upper' ? 'caps' : 'sentence case'} ·{' '}
            {active.headlineTracking >= 0 ? '+' : ''}
            {(active.headlineTracking * 1000).toFixed(0)} tracking.
          </span>
        </p>
      )}

      <div className="mt-3 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
        <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-ink-400">
          Typeface
        </p>
        <p className="mt-1 text-[11px] text-ink-200">Helvetica Neue</p>
        <p className="mt-1 text-[10px] leading-relaxed text-ink-500">
          {fontStatus?.detail ?? 'Checking available weights…'}
        </p>
        {fontStatus && (
          <div className="mt-2 flex flex-wrap gap-1">
            {HN_WEIGHTS.map((w) => {
              const ok = fontStatus.available.includes(w.value);
              return (
                <span
                  key={w.value}
                  title={
                    ok
                      ? `${w.label} resolved`
                      : `${w.label} not found — falls back to the nearest Helvetica Neue weight`
                  }
                  className={`rounded px-1.5 py-0.5 text-[9px] ${
                    ok
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'bg-white/[0.04] text-ink-500 line-through'
                  }`}
                  style={{ fontWeight: w.value }}
                >
                  {w.label}
                </span>
              );
            })}
          </div>
        )}
        <Hint>
          Every style renders in Helvetica Neue — the differences above come from
          weight, scale, spacing and composition, never from another typeface.
          Add licensed files to <code className="text-ink-400">/public/fonts</code>{' '}
          to fill in missing weights.
        </Hint>
      </div>
    </Panel>
  );
}

function weightName(weight: number): string {
  return HN_WEIGHTS.reduce((best, w) =>
    Math.abs(w.value - weight) < Math.abs(best.value - weight) ? w : best,
  ).label;
}
